import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { MemberCard } from "../components/Cards";
import { Search } from "lucide-react";

const TIERS = ["All", "UHNW", "HNW", "Fortune 100 CEO", "Top 100 Investment Manager"];

export default function Directory() {
  const [members, setMembers] = useState([]);
  const [tier, setTier] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data));
  }, []);

  const industries = useMemo(() => {
    const set = new Set(members.map((m) => m.industry));
    return ["All Industries", ...Array.from(set)];
  }, [members]);

  const [industry, setIndustry] = useState("All Industries");

  const filtered = members.filter((m) => {
    if (tier !== "All" && m.tier !== tier) return false;
    if (industry !== "All Industries" && m.industry !== industry) return false;
    if (q) {
      const t = q.toLowerCase();
      return (
        m.name.toLowerCase().includes(t) ||
        m.company.toLowerCase().includes(t) ||
        m.title.toLowerCase().includes(t)
      );
    }
    return true;
  });

  return (
    <div data-testid="directory-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-16 reveal">
        <p className="eyebrow mb-4">The Directory</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6">Profiled Members</h1>
        <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          Magnates, family-office stewards, Fortune Top 100 CEOs and the Top 100 investment managers
          we have profiled, interviewed, or chronicled.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-12 pb-8" style={{ borderBottom: "1px solid var(--hairline)" }}>
        <div className="flex flex-col md:flex-row gap-6 md:items-end">
          <div className="flex-1">
            <p className="eyebrow mb-3">Search</p>
            <div className="flex items-center gap-3" style={{ borderBottom: "1px solid var(--hairline-strong)" }}>
              <Search size={16} style={{ color: "var(--ink-faint)" }} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="By name, company, or title…"
                data-testid="directory-search"
                className="flex-1 bg-transparent border-none outline-none py-3 text-base"
                style={{ color: "var(--ink)" }}
              />
            </div>
          </div>
          <div>
            <p className="eyebrow mb-3">Tier</p>
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              data-testid="directory-tier-filter"
              className="bg-transparent text-sm py-3 px-2 outline-none cursor-pointer"
              style={{ borderBottom: "1px solid var(--hairline-strong)", color: "var(--ink)", minWidth: "240px" }}
            >
              {TIERS.map((t) => <option key={t} value={t} style={{ background: "var(--surface)" }}>{t}</option>)}
            </select>
          </div>
          <div>
            <p className="eyebrow mb-3">Industry</p>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              data-testid="directory-industry-filter"
              className="bg-transparent text-sm py-3 px-2 outline-none cursor-pointer"
              style={{ borderBottom: "1px solid var(--hairline-strong)", color: "var(--ink)", minWidth: "240px" }}
            >
              {industries.map((i) => <option key={i} value={i} style={{ background: "var(--surface)" }}>{i}</option>)}
            </select>
          </div>
        </div>
      </div>

      <p className="text-xs uppercase tracking-[0.2em] mb-8" style={{ color: "var(--ink-faint)" }}>
        {filtered.length} {filtered.length === 1 ? "Member" : "Members"}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filtered.map((m) => <MemberCard key={m.id} member={m} />)}
      </div>

      {filtered.length === 0 && (
        <p className="text-center py-20" style={{ color: "var(--ink-muted)" }}>No members match these filters.</p>
      )}
    </div>
  );
}
